import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import type { CreatePayResult, PlanItem, UpgradeOrder, BankInfo } from './types';
import { fmtPrice } from './utils';

interface Props {
  order: UpgradeOrder;
  plan: PlanItem;
  payResult: CreatePayResult | null;
  paying: boolean;
  onMockPay: () => void;
  /** 银行转账：用户是否已点「我已完成转账」提交确认 */
  transferSubmitted?: boolean;
  /** 银行转账：点「我已完成转账」的回调；不传则不显示该按钮 */
  onSubmitTransfer?: () => void;
  /** 正在轮询确认支付结果（扫码/转账/跳转模式） */
  checking?: boolean;
  /** 轮询超时仍未确认到账 */
  expired?: boolean;
  /** 订单已关闭 */
  closed?: boolean;
  /** 返回上一步重新选套餐/支付方式；不传则不显示该入口 */
  onBack?: () => void;
  /** 关闭结算弹窗：转账提交后的等待态给「关闭」出口；不传则不显示 */
  onClose?: () => void;
}

/** 银行转账收款信息展示 */
function BankTransferInfo({ bankInfo, orderNo }: { bankInfo: BankInfo; orderNo: string }) {
  const rows: Array<[string, string | undefined]> = [
    ['开户银行', bankInfo.bankName],
    ['户名', bankInfo.accountName],
    ['账号', bankInfo.accountNo],
    ['支行', bankInfo.branch],
  ];
  return (
    <div className="bg-orange-50 border border-orange-200 rounded-lg px-4 py-3 space-y-2 text-xs">
      <p className="text-sm font-semibold text-orange-800">
        <i className="ri-bank-card-line mr-1" />
        请按以下信息转账
      </p>
      {rows.map(([label, value]) =>
        value ? (
          <div key={label} className="flex justify-between gap-4">
            <span className="text-orange-700 shrink-0">{label}</span>
            <span className="text-orange-900 font-medium text-right break-all">{value}</span>
          </div>
        ) : null,
      )}
      {bankInfo.instructions && <p className="text-orange-700 pt-1">{bankInfo.instructions}</p>}
      <p className="text-orange-600 pt-1 border-t border-orange-200">转账备注请填写订单编号：{orderNo}</p>
      <p className="text-orange-500">提交转账后，管理员确认到账即自动开通会员。</p>
    </div>
  );
}

/** 微信扫码支付：将 code_url 渲染为可扫描二维码图片（生成失败回退为文本 + 复制） */
function WechatQrcode({ codeUrl }: { codeUrl: string }) {
  const [copied, setCopied] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');

  useEffect(() => {
    let active = true;
    QRCode.toDataURL(codeUrl, { width: 220, margin: 2, color: { dark: '#000000', light: '#ffffff' } })
      .then((url) => {
        if (active) setQrDataUrl(url);
      })
      .catch(() => {
        if (active) setQrDataUrl('');
      });
    return () => {
      active = false;
    };
  }, [codeUrl]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(codeUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* 忽略复制失败 */
    }
  };

  return (
    <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-4 text-center space-y-3">
      <p className="text-sm font-medium text-green-700">
        <i className="ri-wechat-pay-line text-xl mr-1 align-[-3px]" />
        请使用微信扫码支付
      </p>

      {qrDataUrl ? (
        <div className="flex justify-center">
          <img
            src={qrDataUrl}
            alt="微信支付二维码"
            className="w-[200px] h-[200px] bg-white border border-green-200 rounded-lg p-2"
          />
        </div>
      ) : (
        <div className="w-[200px] h-[200px] mx-auto flex items-center justify-center bg-white border border-green-200 rounded-lg text-xs text-green-500">
          <i className="ri-loader-4-line animate-spin mr-1" />
          二维码生成中…
        </div>
      )}

      <div className="flex items-center gap-2 justify-center">
        <code className="text-[11px] text-green-700 bg-white border border-green-200 rounded px-2 py-1 break-all max-w-[220px]">
          {codeUrl}
        </code>
        <button
          type="button"
          onClick={copy}
          className="text-xs px-2 py-1 rounded bg-green-500 hover:bg-green-600 text-white cursor-pointer whitespace-nowrap"
        >
          {copied ? '已复制' : '复制'}
        </button>
      </div>
      <p className="text-xs text-green-500">支付完成后本页将自动确认并开通会员</p>
    </div>
  );
}

/**
 * 支付步骤：订单创建成功后，依据后端返回的支付模式渲染对应交互。
 * - redirect：跳转第三方支付页（支付宝）
 * - qrcode：微信扫码
 * - bank_transfer：展示收款账户
 * - mock：模拟支付按钮（mock 模式或 0 元单）
 */
export function PaymentStep({ order, plan, payResult, paying, onMockPay, transferSubmitted, onSubmitTransfer, checking, expired, closed, onBack, onClose }: Props) {
  const orderNo = order.orderNo ?? order.id?.slice(0, 8) ?? '';
  const discount = Number(order.discountAmount ?? 0);

  return (
    <div className="space-y-4">
      <div className="bg-teal-50 border border-teal-100 rounded-xl px-4 py-2.5 flex items-center gap-3">
        <i className="ri-file-list-3-line text-2xl text-teal-500 shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-teal-700 leading-tight">订单已创建</p>
          <p className="text-xs text-teal-600 truncate">订单编号：{orderNo}</p>
        </div>
      </div>

      {onBack && payResult?.mode !== 'bank_transfer' && (
        <div className="text-center">
          <button
            type="button"
            onClick={onBack}
            className="text-xs text-gray-500 hover:text-gray-700 cursor-pointer"
          >
            <i className="ri-arrow-left-line mr-1" />
            返回上一步，重新选择套餐或支付方式
          </button>
        </div>
      )}

      <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
        <div className="flex justify-between text-gray-600">
          <span>套餐</span>
          <span className="font-medium text-gray-800">
            {plan.name}（{plan.durationMonths} 个月）
          </span>
        </div>
        <div className="flex justify-between text-gray-600">
          <span>原价</span>
          <span>¥{fmtPrice(order.originalAmount ?? plan.price)}</span>
        </div>
        {discount > 0 && (
          <div className="flex justify-between text-green-600">
            <span>优惠减免</span>
            <span>-¥{fmtPrice(discount)}</span>
          </div>
        )}
        <div className="flex justify-between text-gray-800 font-semibold border-t border-gray-200 pt-2">
          <span>实付金额</span>
          <span className="text-amber-600">¥{fmtPrice(order.finalAmount ?? plan.price)}</span>
        </div>
      </div>

      {payResult?.mode === 'redirect' && (
        <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-4 text-center">
          <i className="ri-alipay-line text-3xl text-blue-500 mb-2 inline-block" />
          <p className="text-sm font-medium text-blue-700">正在跳转支付页面…</p>
          <p className="text-xs text-blue-500 mt-1">如未自动跳转，请稍等片刻</p>
        </div>
      )}

      {payResult?.mode === 'qrcode' && <WechatQrcode codeUrl={payResult.codeUrl} />}

      {payResult?.mode === 'bank_transfer' && (
        <div className="space-y-3">
          <BankTransferInfo bankInfo={payResult.bankInfo} orderNo={orderNo} />
          {!transferSubmitted ? (
            <div className="flex items-center gap-3">
              {onBack && (
                <button
                  type="button"
                  onClick={onBack}
                  className="px-5 py-3 rounded-xl text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 whitespace-nowrap cursor-pointer"
                >
                  上一步
                </button>
              )}
              <button
                type="button"
                onClick={onSubmitTransfer}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer"
              >
                <i className="ri-checkbox-circle-line text-sm" />
                我已完成转账，提交确认
              </button>
            </div>
          ) : closed ? (
            <div className="space-y-2">
              <div className="text-xs px-3 py-2 rounded-lg bg-rose-50 text-rose-600">
                <i className="ri-close-circle-line mr-1" />
                该转账已被管理员驳回或订单已关闭，请返回上一步重新下单
              </div>
              {onBack && (
                <button
                  type="button"
                  onClick={onBack}
                  className="w-full py-2.5 rounded-xl text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 cursor-pointer"
                >
                  上一步
                </button>
              )}
            </div>
          ) : (
            <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-5 text-center space-y-3">
              <div className="flex items-center justify-center gap-2 text-sm font-medium text-blue-700">
                <i className="ri-loader-4-line animate-spin text-lg" />
                正在确认支付结果，确认后自动开通会员
              </div>
              <p className="text-xs text-blue-500">转账为非实时确认，请等待管理员核对银行实际到账</p>
              {onClose && (
                <button
                  type="button"
                  onClick={onClose}
                  className="text-xs px-5 py-1.5 rounded-lg text-blue-600 bg-white border border-blue-200 hover:bg-blue-50 cursor-pointer"
                >
                  关闭
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {payResult?.mode === 'mock' && (
        <>
          <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 text-xs text-blue-700">
            <i className="ri-information-line mr-1" />
            当前为模拟支付模式，点击下方按钮模拟支付回调
          </div>
          <button
            type="button"
            onClick={onMockPay}
            disabled={paying}
            className="w-full flex items-center justify-center gap-2 py-3 bg-green-500 hover:bg-green-600 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {paying ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                支付处理中…
              </>
            ) : (
              <>
                <i className="ri-checkbox-circle-line text-sm" />
                模拟支付 · 确认付款
              </>
            )}
          </button>
          <p className="text-xs text-center text-gray-400">在后台切换为真实支付模式后将自动跳转至支付页面</p>
        </>
      )}

      {/* 异步到账模式（扫码/转账/跳转）的支付确认状态 */}
      {payResult && payResult.mode !== 'mock' && payResult.mode !== 'bank_transfer' && checking && (
        <div className="flex items-center justify-center gap-2 text-xs px-3 py-2 rounded-lg bg-blue-50 text-blue-600">
          <i className="ri-loader-4-line animate-spin" />
          正在确认支付结果，请稍候…
        </div>
      )}
      {payResult && payResult.mode !== 'mock' && payResult.mode !== 'bank_transfer' && !checking && closed && (
        <div className="text-xs px-3 py-2 rounded-lg bg-rose-50 text-rose-600">
          <i className="ri-close-circle-line mr-1" />
          订单已关闭，请重新下单
        </div>
      )}
      {payResult && payResult.mode !== 'mock' && payResult.mode !== 'bank_transfer' && !checking && !closed && expired && (
        <div className="text-xs px-3 py-2 rounded-lg bg-amber-50 text-amber-700">
          <i className="ri-time-line mr-1" />
          暂未确认到支付结果；如已付款，请稍后在会员状态中查看，或重新发起支付
        </div>
      )}
    </div>
  );
}
